import cookie from 'js-cookie';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import queryString from 'query-string';
import styled from 'styled-components';
import analytics from 'services/analytics';

import { useSignup } from 'web-services/UserService';
import Login, { MODES } from 'web-ui-kit/LoginResetPassword';

import { USER_INFO_SUCCESS } from 'redux/User';
import { bugsnagError } from 'services/bugsnag';
import { customerLandingPage } from 'services/routing';
import { getUserInfo, getReferralQA, login, signUpPasswordless } from 'services/warehouse/api';
import HeaderMonarchLoggedOutController from 'screens/Studio/Studio/components/HeaderMonarchLoggedOut';

/**
 * Connect up the Login View with the UserService.
 */
export function LoginController ({
  // global redux dispatcher
  dispatch = () => {},

  // the routing history object
  history = {},

  // the routing location object
  location = {},

  // boolean showing if the user is logged in based on state
  userInfo = {},

  // allow passwordless signup
  skipPassword = false,

  // string with the selected packageName on signUp
  packageName = '',

  redirectUrl = ''

}) {
  if (userInfo.loggedIn) {
    return handleLoggedIn({ history, location })(userInfo);
  }
  const [partnerQuestionAnswers, setPartnerQuestionAnswers] = useState([]);
  const [partnerQuestion, setPartnerQuestion] = useState('How did you hear about Modsy? (Optional)');
  const [refferalPartner, setRefferalPartner] = useState('');

  const signupMode = packageName ? 'signup' : 'login';
  const postSignUpAction = {
    type: 'package',
    payload: packageName
  };

  // Connect up to the UserService for logging user in.
  const { currentState, events, send, states } = useSignup({
    logErrorProxy: bugsnagError,
    mode: signupMode,
    signupUserDataProxy: signupMode !== 'login' ? signUpUser(dispatch, postSignUpAction, refferalPartner) : loginUser(dispatch),
    skipPassword,
    onSignedUp: handleLoggedIn({ history, location })
  });

  const { email, password, submitError } = currentState.context;

  // Manage the client error state.
  // For this login we merge all errors into a single error UI.
  const [clientError, setClientError] = useState('');
  useEffect(() => {
    const fetchOptions = async () => {
      const responseBody = await getReferralQA('');
      setPartnerQuestionAnswers(responseBody.options);
      setPartnerQuestion(responseBody.question);
    };

    if (partnerQuestionAnswers.length === 0) {
      fetchOptions();
    }

    if (email.touched && email.error) {
      setClientError(email.error);
    } else if (password.touched && password.error) {
      setClientError(password.error);
    } else {
      setClientError('');
    }
  }, [email, password]);

  // Map View events to UserService.
  const localDispatch = ({ type, payload }) => {
    switch (type) {
      case 'change': {
        const { field, value } = payload;
        send({
          type: events.INPUT_CHANGE,
          payload: {
            input: field,
            value
          }
        });
        break;
      }

      case 'submit': {
        send({ type: events.SUBMIT });
        break;
      }

      default:
        break;
    }
  };

  const loginMode = packageName ? MODES.SIGNUP_PACKAGE_SELECTION : MODES.LOGIN;

  return (
    <LoginWrapper>
      <HeaderMonarchLoggedOutController />
      <Login
        dispatch={localDispatch}
        email={email.value}
        error={clientError || submitError}
        mode={loginMode}
        password={password.value}
        submitting={currentState.active !== states.EDITING}
        Link={Link}
        partnerQuestionAnswers={partnerQuestionAnswers}
        partnerQuestion={partnerQuestion}
        refferalPartner={refferalPartner}
        setRefferalPartner={setRefferalPartner}
        redirectUrl={redirectUrl}
      />
    </LoginWrapper>
  );
}

/**
 * Sign up user.
 */
export function signUpUser (dispatch, postSignUpAction, refferalPartner) {
  return async ({ email, password }) => {
    // sign up the user.
    const referral = refferalPartner;
    const user = await signUpPasswordless({ email, referral });

    let flags = {};
    try {
      await cookie.set('jwtToken', user.token, { expires: 30 });
      const userInfo = await getUserInfo();
      const { payload } = postSignUpAction;

      if (postSignUpAction.type === 'package') {
        dispatch(analytics.userLoggedIn({ ...userInfo, from: 'Direct Purchase', payload }));
      }

      analytics.userRegistered({ userId: userInfo.userId, email, from: 'Direct Purchase' });

      flags = userInfo.flags || {};
      dispatch({
        type: USER_INFO_SUCCESS,
        payload: userInfo
      });
    } catch (error) {
      bugsnagError(error);
      throw new Error('Sign up error. Please try again');
    }

    // Return the user object from signing in along with the feature flags.
    return { ...user, id: user.userId, flags };
  };
}

/**
 * Login user.
 */
function loginUser (dispatch) {
  return async ({ email, password }) => {
    // Login in the user.
    const user = await login({ email, password });

    // We need to fetch the user's info including feature flags
    // as part of the Login process and then update redux auth
    // state with the user info. This requires setting the
    // jwtToken first.
    let flags = {};
    try {
      await cookie.set('jwtToken', user.token, { expires: 30 });
      const userInfo = await getUserInfo();
      flags = userInfo.flags || {};
      dispatch({
        type: USER_INFO_SUCCESS,
        payload: { ...userInfo, loggedIn: true, from: 'Login' }
      });
    } catch (error) {
      bugsnagError(error);
      throw new Error('Login error. Please try again');
    }

    // Return the user object from logging in along with the feature flags.
    return { ...user, id: user.userId, flags };
  };
}

/**
 * Post login actions.
 * If next route query param is provided go to that route,
 * otherwise go to /spaces route.
 */
export function handleLoggedIn ({ history, location }) {
  return (user) => {
    const next = queryString.parse(location.search).next;
    if (next) {
      // We have 2 apps supporting private routes with the next parameter
      // so the safest action is to do a full page refresh after logging
      // in so the CDN can route to the correct app.
      window.location.href = next;
      return;
    }

    const {
      isProspect,
      flags: { fe_monarch_post_login_redirect: redirectToLandingPage }
    } = user;

    if (redirectToLandingPage) {
      if (isProspect) {
        window.location.href = '/';
        return;
      } else {
        window.location.href = customerLandingPage;
        return;
      }
    }

    const DEFAULT_NEXT = '/spaces';
    history.push(DEFAULT_NEXT);
  };
}

function mapStateToProps (state) {
  return { userInfo: state.auth.userInfo };
}

const LoginWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
LoginWrapper.displayName = 'LoginWrapper';

export default connect(mapStateToProps)(LoginController);
